import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {omit} from 'ramda';

import {TextArea as DXTextArea} from 'devextreme-react';


export default class TextArea extends Component {
    constructor(props) {
        super(props);

        this.onValueChanged = this.onValueChanged.bind(this);
    }

    onValueChanged(e) {
        const {setProps} = this.props;

        if (setProps) setProps({value: e.value}); // e.text
    }

    render() {
        return (
            <DXTextArea
                {...omit(['setProps'], this.props)}
                onValueChanged={this.props.onValueChanged || this.onValueChanged}
            />
        );
    }
}

TextArea.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks
     */
    id: PropTypes.string,
    /**
     * A collection of an node's child elements
     */
    children: PropTypes.node,

    /**
     * Specifies the shortcut key that sets focus on the UI component
     */
    accessKey: PropTypes.string,
    /**
     * Specifies whether or not the UI component changes its state when interacting with a user
     */
    activeStateEnabled: PropTypes.bool,
    /**
     * A Boolean value specifying whether or not the auto resizing mode is enabled
     */
    autoResizeEnabled: PropTypes.bool,
    /**
     * Specifies whether the UI component responds to user interaction
     */
    disabled: PropTypes.bool,
    /**
     * Specifies the global attributes to be attached to the UI component's container element
     */
    elementAttr: PropTypes.object,
    /**
     * Specifies whether the UI component can be focused using keyboard navigation
     */
    focusStateEnabled: PropTypes.bool,
    /**
     * Specifies the UI component's height
     */
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.func]),
    /**
     * Specifies text for a hint that appears when a user pauses on the UI component
     */
    hint: PropTypes.string,
    /**
     * Specifies whether the UI component changes its state when a user pauses on it
     */
    hoverStateEnabled: PropTypes.bool,
    /**
     * Specifies the attributes to be passed on to the underlying HTML element
     */
    inputAttr: PropTypes.object,
    /**
     * Specifies or indicates whether the editor's value is valid
     */
    isValid: PropTypes.bool,
    /**
     * Specifies the maximum height of the UI component
     */
    maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * Specifies the maximum number of characters you can enter into the textbox
     */
    maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * Specifies the minimum height of the UI component
     */
    minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * The value to be assigned to the name attribute of the underlying HTML element
     */
    name: PropTypes.string,
    /**
     * A function that is executed when the UI component loses focus after the text field's content was changed using the keyboard
     */
    onChange: PropTypes.func,
    /**
     * A function that is executed when the UI component's content is ready and each time the content is changed
     */
    onContentReady: PropTypes.func,
    /**
     * A function that is executed when the UI component's input has been copied
     */
    onCopy: PropTypes.func,
    /**
     * A function that is executed when the UI component's input has been cut
     */
    onCut: PropTypes.func,
    /**
     * A function that is executed before the UI component is disposed of
     */
    onDisposing: PropTypes.func,
    /**
     * A function that is executed when the Enter key has been pressed while the UI component is focused
     */
    onEnterKey: PropTypes.func,
    /**
     * A function that is executed when the UI component gets focus
     */
    onFocusIn: PropTypes.func,
    /**
     * A function that is executed when the UI component loses focus
     */
    onFocusOut: PropTypes.func,
    /**
     * A function used in JavaScript frameworks to save the UI component instance
     */
    onInitialized: PropTypes.func,
    /**
     * A function that is executed each time the UI component's input is changed while the UI component is focused
     */
    onInput: PropTypes.func,
    /**
     * A function that is executed when a user is pressing a key on the keyboard
     */
    onKeyDown: PropTypes.func,
    /**
     * A function that is executed when a user releases a key on the keyboard
     */
    onKeyUp: PropTypes.func,
    /**
     * A function that is executed after a UI component property is changed
     */
    onOptionChanged: PropTypes.func,
    /**
     * A function that is executed when the UI component's input has been pasted
     */
    onPaste: PropTypes.func,
    /**
     * A function that is executed after the UI component's value is changed
     */
    onValueChanged: PropTypes.func,
    /**
     * The text displayed by the UI component when the UI component value is empty
     */
    placeholder: PropTypes.string,
    /**
     * Specifies whether the editor is read-only
     */
    readOnly: PropTypes.bool,
    /**
     * Switches the UI component to a right-to-left representation
     */
    rtlEnabled: PropTypes.bool,
    /**
     * Specifies whether or not the UI component checks the inner text for spelling mistakes
     */
    spellcheck: PropTypes.bool,
    /**
     * Specifies how the UI component's text field is styled
     */
    stylingMode: PropTypes.oneOf(['outlined', 'underlined', 'filled']),
    /**
     * Specifies the number of the element when the Tab key is used for navigating
     */
    tabIndex: PropTypes.number,
    /**
     * Information on the broken validation rule. Contains the first item from the validationErrors array
     */
    validationError: PropTypes.object,
    /**
     * An array of the validation rules that failed
     */
    validationErrors: PropTypes.object,
    /**
     * Specifies how the message about the validation rules that are not satisfied by this editor's value is displayed
     */
    validationMessageMode: PropTypes.oneOf(['always', 'auto']),
    /**
     * Indicates or specifies the current validation status
     */
    validationStatus: PropTypes.oneOf(['valid', 'invalid', 'pending']),
    /**
     * Specifies a value the UI component displays
     */
    value: PropTypes.string,
    /**
     * Specifies the DOM events after which the UI component's value should be updated
     */
    valueChangeEvent: PropTypes.string,
    /**
     * Specifies whether the UI component is visible
     */
    visible: PropTypes.bool,
    /**
     * Specifies the UI component's width
     */
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.func]),

    /**
     * Dash-assigned callback that gets fired when the value changes.
     */
    setProps: PropTypes.func,
};

TextArea.defaultProps = {
    accessKey: null,
    activeStateEnabled: false,
    autoResizeEnabled: false,
    disabled: false,
    elementAttr: {},
    focusStateEnabled: true,
    // height: undefined,
    // hint: undefined,
    hoverStateEnabled: true,
    inputAttr: {},
    isValid: true,
    // maxHeight: undefined,
    maxLength: null,
    // minHeight: undefined,
    name: '',
    onChange: null,
    onContentReady: null,
    onCopy: null,
    onCut: null,
    onDisposing: null,
    onEnterKey: null,
    onFocusIn: null,
    onFocusOut: null,
    onInitialized: null,
    onInput: null,
    onKeyDown: null,
    onKeyUp: null,
    onOptionChanged: null,
    onPaste: null,
    onValueChanged: null,
    placeholder: '',
    readOnly: false,
    rtlEnabled: false,
    spellcheck: false,
    stylingMode: 'outlined',
    tabIndex: 0,
    // validationError: undefined,
    // validationErrors: undefined,
    validationMessageMode: 'auto',
    validationStatus: 'valid',
    value: '',
    valueChangeEvent: 'change',
    visible: true,
    width: undefined
};
